import { inject, Injectable } from '@angular/core';
import { Client } from '@df/core/service/client';
import cloneDeep from 'lodash-es/cloneDeep';
import defaultsDeep from 'lodash-es/defaultsDeep';
import isFunction from 'lodash-es/isFunction';
import range from 'lodash-es/range';
import set from 'lodash-es/set';
import sortBy from 'lodash-es/sortBy';
import { NICmsContentComponent } from './interfaces/icms-content-component.interfaces';
import { NICmsContentConfig } from './interfaces/icms-content-config.interfaces';
import { NICmsContentField } from './interfaces/icms-content-field.interfaces';
import { NICmsContentPageType } from './interfaces/icms-content-page-type.interfaces';
import { DEFAULT_PAGE_FIELDS } from './page/default-page-fields';

type TComponentMetaPatch = (meta: NICmsContentComponent.IMeta) => void;
type TPageMetaPatch = (meta: NICmsContentPageType.IMeta) => void;

@Injectable({ providedIn: 'root' })
export class ICmsContentService {
    /**
     * Map of components
     * keys are components ids
     * values are their meta
     */
    private static components: { [id: string]: NICmsContentComponent.IMeta } = {};
    /**
     * Map of page types
     * keys are page type names
     * values are their meta
     */
    static pageTypes: { [id: string]: NICmsContentPageType.IMeta } = {};
    /**
     * Extra field patches
     */
    private static pageMetaPatches: TPageMetaPatch[] = [];
    /**
     * Map of configs
     * keys are config ids
     * values are their meta
     */
    static configs: { [id: string]: NICmsContentConfig.IMeta<any> } = {};
    /**
     * Extra field patches
     */
    private static componentFieldPatches: TComponentMetaPatch[] = [];

    private client = inject(Client);

    /**
     * Registers component to be used with iCMS
     */
    static registerComponent(component: NICmsContentComponent.IMeta) {
        ICmsContentService.components[component.id] = component;
        ICmsContentService.componentFieldPatches.forEach(cb => cb(component));
    }

    static registerComponentMetaPatch(cb: TComponentMetaPatch) {
        ICmsContentService.componentFieldPatches.push(cb);
        Object.values(ICmsContentService.components).forEach(cb);
    }

    /**
     * Registers page type
     */
    static registerPageType(pageType: NICmsContentPageType.IMeta) {
        pageType.fields = [...DEFAULT_PAGE_FIELDS, ...(pageType.fields || [])];
        ICmsContentService.pageTypes[pageType.id] = pageType;
        ICmsContentService.pageMetaPatches.forEach(cb => cb(pageType));
    }

    static registerPageMetaPatch(cb: TPageMetaPatch) {
        ICmsContentService.pageMetaPatches.push(cb);
        Object.values(ICmsContentService.pageTypes).forEach(cb);
    }

    /**
     * Registers config to be used with iCMS
     */
    static registerConfig(config: NICmsContentConfig.IMeta<any>) {
        ICmsContentService.configs[config.id] = config;
    }

    /**
     * Gets component metadata
     */
    getComponentMeta(componentId: string): NICmsContentComponent.IMeta | undefined {
        return ICmsContentService.components[componentId];
    }

    /**
     * Gets component metadata
     */
    getComponentMetaByType<T extends NICmsContentComponent.IComponent = any>(
        type: NICmsContentComponent.ICmsComponentType<T>
    ): NICmsContentComponent.IMeta<T> | undefined {
        return this.getComponentMetas().find(i => i.type === type) as NICmsContentComponent.IMeta<T> | undefined;
    }

    /**
     * Get page type data
     */
    getPageType(pageTypeId: string | undefined): NICmsContentPageType.IMeta | undefined {
        return ICmsContentService.pageTypes[pageTypeId || NICmsContentPageType.defaultPageTypeId];
    }

    /**
     * Gets component config
     */
    getConfig(configId: string): NICmsContentConfig.IMeta<any> | undefined {
        return ICmsContentService.configs[configId];
    }

    /**
     * Gets list of all configurations
     */
    getConfigs(): NICmsContentConfig.IMeta<any>[] {
        return Object.values(ICmsContentService.configs);
    }

    /**
     * Gets all components metas
     */
    getComponentMetas(): NICmsContentComponent.IMeta[] {
        return Object.values(ICmsContentService.components);
    }

    /**
     * Gets fields for page type
     * if tab is provided then only that tab fields will be used
     */
    getPageTypeFields(pageTypeId: string | undefined, tab?: string): NICmsContentPageType.IField[] {
        const meta = this.getPageType(pageTypeId);
        if (!meta) {
            return [];
        }
        const metaFields = meta.fields || [];
        const fields: NICmsContentPageType.IField[] = tab ? metaFields.filter(field => field.tab === tab) : metaFields;
        return sortBy(fields, 'priority') as any as NICmsContentPageType.IField[];
    }

    /**
     * Add component to the bottom of outlet
     */
    initFieldset<TData = any, TContext = any>(
        fields: NICmsContentField.IField[],
        context?: TContext,
        data: Partial<TData> = {},
        initialData?: Partial<TData>
    ): Partial<TData> {
        fields.forEach(field => {
            if (Object.prototype.hasOwnProperty.call(field, 'default')) {
                set(data, field.key, isFunction(field.default) ? field.default(context) : cloneDeep(field.default));
            } else {
                switch (field.type) {
                    case NICmsContentField.EFieldType.repeater:
                        if ((field as NICmsContentField.IRepeaterField).defaultSize === 0) {
                            return;
                        }
                        const sample = this.initFieldset((field as NICmsContentField.IRepeaterField).fields, context);
                        set(
                            data,
                            field.key,
                            range(0, (field as NICmsContentField.IRepeaterField).defaultSize || 1).map(i => cloneDeep(sample))
                        );
                        break;
                    case NICmsContentField.EFieldType.object:
                        set(data, field.key, cloneDeep(this.initFieldset((field as NICmsContentField.IObjectField).fields, context)));
                        break;
                    case NICmsContentField.EFieldType.components:
                        set(data, field.key, []);
                        break;
                }
            }
        });
        if (initialData) {
            // const copy = this.extractNested(initialData);
            defaultsDeep(data, cloneDeep(initialData));
        }
        return data;
    }
}
